import React from 'react'
import { Tooltip } from '@mui/material'
import { FileDown, Paperclip, Search } from 'lucide-react'
import Button, { IconButton } from '../ui/Button'

type CodeIndexToolbarProps = {
  searchQuery: string
  onSearchChange: (value: string) => void
  showDiffToggle?: boolean
  diffVisible?: boolean
  diffToggleDisabled?: boolean
  onDiffVisibleChange?: (visible: boolean) => void
  canOpenAppendix: boolean
  canSplit: boolean
  canMerge: boolean
  deleteDisabled: boolean
  deleteTooltip: string
  onOpenAppendix: () => void
  onSplit: () => void
  onMerge: () => void
  onDelete: () => void
  onExportPdf: () => void
  exportPdfDisabled?: boolean
}

export const CodeIndexToolbar: React.FC<CodeIndexToolbarProps> = ({
  searchQuery,
  onSearchChange,
  showDiffToggle = false,
  diffVisible = false,
  diffToggleDisabled = false,
  onDiffVisibleChange,
  canOpenAppendix,
  canSplit,
  canMerge,
  deleteDisabled,
  deleteTooltip,
  onOpenAppendix,
  onSplit,
  onMerge,
  onDelete,
  onExportPdf,
  exportPdfDisabled
}) => {
  return (
    <div className="flex items-center justify-start mb-3 gap-3">
      <div className="flex flex-col gap-1">
        <span className="text-xs text-[#1D5176] text-left">Search</span>
        <div className="relative">
          <input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Keywords"
            className="w-[260px] border border-[#CBD5E1] py-2 pl-3 pr-8 rounded-md outline-none"
          />
          <Search className="absolute right-2 top-2 w-4 h-4 text-[#94A3B8]" />
        </div>
      </div>
      <div className="flex items-center gap-3 ml-auto">
        {showDiffToggle ? (
          <label className={`inline-flex items-center gap-2 text-sm font-medium ${diffToggleDisabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-gray-700'}`}>
            <input
              type="checkbox"
              role="switch"
              aria-label="Show diff"
              checked={diffVisible}
              disabled={diffToggleDisabled}
              onChange={(event) => onDiffVisibleChange?.(event.target.checked)}
              className="peer sr-only"
            />
            <span className="relative h-5 w-9 rounded-full bg-gray-300 transition-colors peer-checked:bg-[#1D74D8] peer-disabled:opacity-60">
              <span className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${diffVisible ? 'translate-x-4' : ''}`} />
            </span>
            <span>Show diff</span>
          </label>
        ) : null}
        <Button
          size="sm"
          variant={canOpenAppendix ? 'secondary' : 'muted'}
          onClick={onOpenAppendix}
          disabled={!canOpenAppendix}
          leftIcon={<Paperclip className="w-4 h-4 -scale-x-100" aria-hidden="true" />}
        >
          Appendix
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={onSplit}
          disabled={!canSplit}
          leftIcon={<img src="/splitIcon.svg" alt="Split" className="w-4 h-4" />}
        >
          Split
        </Button>
        <Button
          size="sm"
          variant={canMerge ? "primary" as const : "muted" as const}
          onClick={onMerge}
          disabled={!canMerge}
          leftIcon={<img src="/mergeIcon.svg" alt="Merge" className={`w-4 h-4 ${canMerge ? 'brightness-0 invert' : ''}`} />}
        >
          Merge
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={onExportPdf}
          disabled={exportPdfDisabled}
          leftIcon={<FileDown className="w-4 h-4" />}
        >
          Export to PDF
        </Button>
        <Tooltip title={deleteTooltip}>
          <span>
            <IconButton
              size="sm"
              variant="danger"
              icon={<img src="/trashIcon.svg" alt="Delete" className="w-5 h-5" />}
              onClick={onDelete}
              disabled={deleteDisabled}
              title={deleteTooltip}
            />
          </span>
        </Tooltip>
      </div>
    </div>
  )
}

